import {
  ChangeDetectionStrategy,
  Component,
  EventEmitter,
  Input,
  OnChanges,
  OnDestroy,
  OnInit,
  Output,
  SimpleChanges
} from '@angular/core';
import { AuthService } from '@features/auth/auth.service';
import { FilterTypeFields } from '@features/scheduler/scheduler-container/filters/filterTypes';
import { SumHours } from '@features/scheduler/scheduler.model';
import { SchedulerService } from '@features/scheduler/scheduler.service';
import { FilterStorageModel } from '@shared/interfaces/filtersStore.model';
import { getISOWeek } from 'date-fns';
import { Subject } from 'rxjs';
import { filter, takeUntil } from 'rxjs/operators';

import { legacyParse } from '@date-fns/upgrade/v2';

@Component({
  selector: 'app-sum-hours',
  templateUrl: './sum-hours.component.html',
  styleUrls: ['./sum-hours.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class SumHoursComponent implements OnChanges, OnInit, OnDestroy {
  @Input() filters: FilterStorageModel;
  @Input() fromFilterKey: string = 'fromTime';
  @Input() toFilterKey: string = 'toTime';
  @Input() timesheetDays: any;
  @Input() isWorkerView = false;
  @Input() onlyMyHours = false;
  @Input() showWeekLabel: boolean;
  @Output() onUserSumLoaded: EventEmitter<{ [key: string]: SumHours }> = new EventEmitter(); // pass to interfaces
  weekNumber: number;
  filterTypesEnum = FilterTypeFields;
  sumHours = new Map<string, SumHours>(); // use interface
  private destroyed$ = new Subject();

  get sumHoursLoading$(): any {
    return this._schedulerService.workSumHours.loading$;
  }

  private setObjProp(obj, key, val): object {
    if (val) {
      obj[key] = val;
    }
    return obj;
  }

  getSumHours() {
    this.sumHours.clear();
    const query = {
      from_time: this.filters.get(this.fromFilterKey),
      to_time: this.filters.get(this.toFilterKey)
    };
    this.setObjProp(query, 'user_id[]', this.filters.get(this.filterTypesEnum.Workers));
    this.setObjProp(query, 'project_id[]', this.filters.get(this.filterTypesEnum.Project));
    this.setObjProp(query, 'client_id[]', this.filters.get(this.filterTypesEnum.W_Client));
    this.setObjProp(query, 'task_id[]', this.filters.get(this.filterTypesEnum.Task));
    if (this.isWorkerView) {
      this.setObjProp(query, 'is_published', true);
      this.setObjProp(query, 'user_id[]', [this._auth.currentUser.id]);
    }

    if (this.onlyMyHours) {
      this.setObjProp(query, 'my_scheduler', 1);
    }

    this._schedulerService.workSumHours
      .get({ query })
      .pipe(takeUntil(this.destroyed$))
      .subscribe(({ data }) => {
        this.onUserSumLoaded.emit(data?.user_sum_data);
        this.sumHours.set('sum', {
          work: data?.works || 0,
          hours: data?.hours || 0
        });

        if (!this.isWorkerView && data?.sum) {
          Object.entries(data.sum).forEach(([key, value]: [string, any]) => {
            this.sumHours.set(key, {
              work: value?.work || 0,
              hours: value?.hours || 0
            });
          });
        }

        if (this.showWeekLabel) {
          this.weekNumber = getISOWeek(legacyParse(this.filters.get(this.filterTypesEnum.FromDate)));
        }
      });
  }

  private reloadSumHoursSubscription() {
    this._schedulerService.$reloadSumHours
      .pipe(
        takeUntil(this.destroyed$),
        filter((val) => !!val)
      )
      .subscribe(() => this.getSumHours());
  }

  ngOnInit() {
    this.reloadSumHoursSubscription();
  }

  ngOnChanges(changes: SimpleChanges) {
    if ((changes?.filters?.currentValue || this.isWorkerView) && changes?.timesheetDays?.currentValue) {
      this.getSumHours();
    }
  }

  ngOnDestroy() {
    this.destroyed$.next(true);
  }

  constructor(
    private _schedulerService: SchedulerService,
    private _auth: AuthService
  ) {}
}
